import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, User, Calendar, MapPin, Heart, Settings, AlertCircle } from 'lucide-react'
import { api } from '../services/api'
import { Report } from '../types/reports'
import { ActionModal } from './ActionModal'


interface SafeUserProfile {
  id: string
  email: string
  first_name: string
  last_name: string
  personality?: string
  created_at: string
  updated_at?: string
  is_premium: boolean
  max_views: number
  max_matches: number
  bio: string
  intro_video_url?: string
  country?: string
  city?: string
  date_of_birth: string
  passions?: string
  profile_completion_percentage: number
  coach: string
  appearance: string
  gender: string
  interested_in: string
  notifications_enabled: boolean
}

interface ReportWithUserProfile {
  report: Report
  reported_user: SafeUserProfile
}

interface ReportDetailsModalProps {
  report: Report
  onClose: () => void
}

export function ReportDetailsModal({ report, onClose }: ReportDetailsModalProps) {
  const [showActionModal, setShowActionModal] = useState(false)
  const [actionType, setActionType] = useState<'block' | 'ignore' | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<ReportWithUserProfile>({
    queryKey: ['report-details', report.id],
    queryFn: async () => {
      const response = await api.get(`/admin/reports/${report.id}`)
      return response.data
    },
  })

  const blockMutation = useMutation({
    mutationFn: async ({ reportId, reason }: { reportId: string; reason: string }) => {
      await api.post(`/admin/reports/${reportId}/block`, { reason })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['report-details', report.id] })
      setShowActionModal(false)
      setActionType(null)
    },
  })

  const ignoreMutation = useMutation({
    mutationFn: async ({ reportId }: { reportId: string }) => {
      await api.post(`/admin/reports/${reportId}/ignore`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['report-details', report.id] })
      setShowActionModal(false)
      setActionType(null)
    },
  })

  const handleDeleteAccount = () => {
    setActionType('block')
    setShowActionModal(true)
  }

  const handleIgnore = () => {
    setActionType('ignore')
    setShowActionModal(true)
  }

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    
    return age
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading report details</h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error instanceof Error ? error.message : 'An error occurred'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const { report: reportData, reported_user: user } = data

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Report Details</h2>
            <div className="flex items-center space-x-4">
              {reportData.status === 'open' && (
                <>
                  <button
                    onClick={handleDeleteAccount}
                    className="btn btn-danger btn-sm"
                    disabled={blockMutation.isPending || ignoreMutation.isPending}
                  >
                    Delete Account
                  </button>
                  <button
                    onClick={handleIgnore}
                    className="btn btn-secondary btn-sm"
                    disabled={blockMutation.isPending || ignoreMutation.isPending}
                  >
                    Ignore Report
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Report Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Report Information</h3>
              </div>
              <div className="card-content">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Report ID:</span>
                    <span className="ml-2 font-mono">{reportData.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Reporter:</span>
                    <span className="ml-2 font-mono">{reportData.reporter_user_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className={`ml-2 font-medium ${
                      reportData.status === 'open' ? 'text-yellow-600' :
                      reportData.status === 'resolved' ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      {reportData.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Created:</span>
                    <span className="ml-2">{formatDate(reportData.created_at)}</span>
                  </div>
                </div>
                
                {reportData.description && (
                  <div className="mt-4">
                    <span className="text-gray-600 text-sm">Description:</span>
                    <p className="mt-1 text-gray-900 whitespace-pre-wrap">{reportData.description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Reported User Profile - Full Profile Display */}
            <div className="space-y-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold">Reported User Profile</h3>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* User Info Card */}
                <div className="lg:col-span-1">
                  <div className="card">
                    <div className="card-header">
                      <div className="flex items-center space-x-4">
                        <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
                          <User className="h-8 w-8 text-red-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">
                            {user.first_name} {user.last_name}
                          </h2>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="card-content space-y-4">
                      <div className="flex items-center text-sm">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="text-gray-600">Age: {calculateAge(user.date_of_birth)} years old</span>
                      </div>
                      
                      {(user.country || user.city) && (
                        <div className="flex items-center text-sm">
                          <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="text-gray-600">
                            {user.city && user.country ? `${user.city}, ${user.country}` : user.city || user.country}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center text-sm">
                        <Heart className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="text-gray-600">Interested in: {user.interested_in}</span>
                      </div>

                      <div className="flex items-center text-sm">
                        <Settings className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="text-gray-600">Coach: {user.coach}</span>
                      </div>

                      <div className="pt-4 border-t">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Profile Completion</span>
                          <span className="font-medium">{user.profile_completion_percentage}%</span>
                        </div>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-red-600 h-2 rounded-full" 
                            style={{ width: `${user.profile_completion_percentage}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="pt-4 border-t">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Premium:</span>
                            <span className={`ml-2 font-medium ${user.is_premium ? 'text-green-600' : 'text-gray-900'}`}>
                              {user.is_premium ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Max Views:</span>
                            <span className="ml-2 font-medium">{user.max_views}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Max Matches:</span>
                            <span className="ml-2 font-medium">{user.max_matches}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Notifications:</span>
                            <span className={`ml-2 font-medium ${user.notifications_enabled ? 'text-green-600' : 'text-gray-900'}`}>
                              {user.notifications_enabled ? 'On' : 'Off'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Profile Details */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Bio */}
                  {user.bio && (
                    <div className="card">
                      <div className="card-header">
                        <h3 className="text-lg font-semibold">Bio</h3>
                      </div>
                      <div className="card-content">
                        <p className="text-gray-700 whitespace-pre-wrap">{user.bio}</p>
                      </div>
                    </div>
                  )}

                  {/* Personality */}
                  {user.personality && (
                    <div className="card">
                      <div className="card-header">
                        <h3 className="text-lg font-semibold">Personality</h3>
                      </div>
                      <div className="card-content">
                        <p className="text-gray-700 whitespace-pre-wrap">{user.personality}</p>
                      </div>
                    </div>
                  )}

                  {/* Passions */}
                  {user.passions && (
                    <div className="card">
                      <div className="card-header">
                        <h3 className="text-lg font-semibold">Passions</h3>
                      </div>
                      <div className="card-content">
                        <div className="flex flex-wrap gap-2">
                          {user.passions.split(';').map((passion, index) => (
                            <span key={index} className="badge badge-info">
                              {passion.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Intro Video */}
                  {user.intro_video_url && (
                    <div className="card">
                      <div className="card-header">
                        <h3 className="text-lg font-semibold">Intro Video</h3>
                      </div>
                      <div className="card-content">
                        <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                          <video
                            controls
                            className="w-full h-full rounded-lg"
                            src={`${api.defaults.baseURL}${user.intro_video_url}`}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Account Info */}
                  <div className="card">
                    <div className="card-header">
                      <h3 className="text-lg font-semibold">Account Information</h3>
                    </div>
                    <div className="card-content">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">User ID:</span>
                          <span className="ml-2 font-mono text-gray-900">{user.id}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Gender:</span>
                          <span className="ml-2 text-gray-900">{user.gender}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Appearance:</span>
                          <span className="ml-2 text-gray-900 capitalize">{user.appearance}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Member Since:</span>
                          <span className="ml-2 text-gray-900">
                            {new Date(user.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {user.updated_at && (
                          <div>
                            <span className="text-gray-600">Last Updated:</span>
                            <span className="ml-2 text-gray-900">
                              {new Date(user.updated_at).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && actionType && data && (
        <ActionModal
          report={data.report}
          actionType={actionType}
          onClose={() => {
            setShowActionModal(false)
            setActionType(null)
          }}
          onBlock={blockMutation.mutate}
          onIgnore={ignoreMutation.mutate}
          isLoading={blockMutation.isPending || ignoreMutation.isPending}
        />
      )}
    </div>
  )
}
